import { t } from '@transifex/native';
import { memo } from 'react';

import { cssClasses } from 'pkg/css/utils';
import type { CalendarDayType, CalendarMonthType } from 'pkg/datetime';
import DateTime from 'pkg/datetime';

import * as css from './styles.css';

interface CalendarWeekdaysProps {
	calendar: CalendarMonthType;
	// When set, a weekday heading selects every selectable day in its column.
	onSelectWeekday?: (weekdayIndex: number) => void;
}

interface CalendarWeekdayProps {
	calendarDay: CalendarDayType;
	index: number;
	onSelect?: (weekdayIndex: number) => void;
}

const CalendarWeekday = ({
	calendarDay,
	index,
	onSelect,
}: CalendarWeekdayProps) => {
	const date = new Date(calendarDay.timestamp);
	const dateTime = new DateTime(date);
	const label = dateTime.toWeekdayString();

	if (!onSelect) {
		return <div className={css.calendarWeekday}>{label}</div>;
	}

	const handleClick = () => onSelect(index);

	// The visible label is a 2-3 letter abbreviation, so spell the action out for
	// assistive tech and as a tooltip.
	const description = t('Select every {weekday} this month', {
		weekday: dateTime.toLocaleDateString({ weekday: 'long' }),
	});

	return (
		<button
			type="button"
			className={cssClasses(css.calendarWeekday, css.clickable)}
			onClick={handleClick}
			title={description}
			aria-label={description}
			data-testid={`calendar.weekday.${index}`}>
			{label}
		</button>
	);
};

export const CalendarWeekdays = memo(
	({ calendar, onSelectWeekday }: CalendarWeekdaysProps) => {
		if (calendar.length === 0) return null;

		// @NOTE Get first week of current month
		const weekdays = calendar.slice(0, 7);

		return (
			<header className={css.weekdays}>
				{weekdays.map((wd, index) => (
					<CalendarWeekday
						key={index}
						calendarDay={wd}
						index={index}
						onSelect={onSelectWeekday}
					/>
				))}
			</header>
		);
	}
);
